import { type ReactNode } from "react";

import { Extension } from "@tiptap/core";
import { Plugin, PluginKey, TextSelection } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

import type { DocumentModel } from "@/models/DocumentModel";
import type { Range } from "@tiptap/core";
import type { Node as ProsemirrorNode } from "@tiptap/pm/model";
import type { EditorState } from "@tiptap/pm/state";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    editorPopover: {
      openPopover: (render: EditorPopoverRender) => ReturnType;
      closePopover: () => ReturnType;
    };
  }
}

export type EditorPopoverRender = (close: () => void) => ReactNode;

export type EditorPopoverState =
  | (Range & {
      render: EditorPopoverRender;
    })
  | null;

type EditorPopoverMeta = { type: "open"; render: EditorPopoverRender } | { type: "close" };

export const editorPopoverKey = new PluginKey<EditorPopoverState>("editorPopover");

/**
 * Render a React component in a floating popover, anchored to the current
 * prosemirror selection. The selection range is wrapped in an inline decoration so it stays
 * highlighted and tracks the text through edits.
 *
 * The actual React content is rendered by the `EditorPopoverView` component.
 */
export const EditorPopover = Extension.create({
  name: "editorPopover",

  addProseMirrorPlugins() {
    function endInsideTextblock(doc: ProsemirrorNode, from: number, to: number): number {
      const $to = doc.resolve(to);

      if ($to.parent.isTextblock && $to.parentOffset > 0) {
        return to;
      }

      const searchFrom = $to.parent.isTextblock ? $to.before() : to;
      const near = TextSelection.near(doc.resolve(searchFrom), -1).to;

      return near > from ? near : to;
    }

    return [
      new Plugin<EditorPopoverState>({
        key: editorPopoverKey,
        state: {
          init: () => null,
          apply(tr, value): EditorPopoverState {
            const meta = tr.getMeta(editorPopoverKey) as EditorPopoverMeta | undefined;

            if (meta?.type === "open") {
              const { from, to } = tr.selection;

              if (from === to) {
                return null;
              }

              return {
                render: meta.render,
                from,
                to: endInsideTextblock(tr.doc, from, to),
              };
            }

            if (meta?.type === "close" || !value) {
              return null;
            }

            if (tr.docChanged) {
              const from = tr.mapping.map(value.from);
              const to = tr.mapping.map(value.to);

              return from >= to ? null : { ...value, from, to };
            }

            return value;
          },
        },
        props: {
          decorations(state) {
            const value = editorPopoverKey.getState(state);

            if (!value) {
              return null;
            }

            return DecorationSet.create(state.doc, [
              Decoration.inline(value.from, value.to, {
                style: "background-color: var(--color-selected-bg)",
              }),
            ]);
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      openPopover:
        (render) =>
        ({ tr, dispatch }) => {
          dispatch?.(tr.setMeta(editorPopoverKey, { type: "open", render }));
          return true;
        },
      closePopover:
        () =>
        ({ tr, dispatch }) => {
          dispatch?.(tr.setMeta(editorPopoverKey, { type: "close" }));
          return true;
        },
    };
  },
});

export function getPopoverRange(state: EditorState): Range | null {
  const value = editorPopoverKey.getState(state);

  if (value) {
    return { from: value.from, to: value.to };
  }

  return null;
}

export function mapPopoverRangeToTextSelection(doc: DocumentModel): TextSelection | null {
  const { tiptap } = doc.editor;
  const range = tiptap ? getPopoverRange(tiptap.state) : null;

  if (!tiptap || !range) {
    return null;
  }

  return TextSelection.create(tiptap.state.doc, range.from, range.to);
}
